import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { useDispatch } from 'react-redux';
import { handleGetRequest } from '../../../service/GetTemplate';
import { endPoints } from '../../../Config';
import usePreferredLanguage from '../../../utilities/usePreferredLanguage';
import { translateUiText } from '../../../utilities/siteTranslations';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

const RecurrentFailuresReport = () => {
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);
    const [recurrentFailures, setRecurrentFailures] = useState([]);
    const dispatch = useDispatch();

    useEffect(() => {
        const loadRecurrent = async () => {
            const params = new URLSearchParams({
                minCalls: '5',
                windowDays: '15'
            });

            const response = await handleGetRequest(endPoints.getRecurrentFailures(params.toString()), true, false, dispatch);

            if (response?.data) {
                const list = (response.data || []).map((r, idx) => ({
                    id: r.machineId || idx,
                    machineId: r.machineId || '-',
                    machineType: r.machineType || '-',
                    customer: r.customer || '-',
                    location: r.location || '-',
                    serviceCalls: r.serviceCalls ?? 0,
                    daysPeriod: r.daysPeriod ?? 15,
                    lastServiceDate: r.lastServiceDate
                        ? new Date(r.lastServiceDate).toISOString().split('T')[0]
                        : '-',
                    commonIssues: r.commonIssues && r.commonIssues.length > 0 ? r.commonIssues : ['-'],
                    status: r.status || 'Low Priority'
                }));

                setRecurrentFailures(list);
            } else {
                setRecurrentFailures([]);
            }
        };

        loadRecurrent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const statusBodyTemplate = (rowData) => {
        const statusColors = {
            'High Priority': '#F44336',
            'Medium Priority': '#FF9800',
            'Low Priority': '#4CAF50'
        };
        return (
            <span style={{ color: statusColors[rowData.status] || '#000', fontWeight: 'bold' }}>
                {t(rowData.status)}
            </span>
        );
    };

    const issuesBodyTemplate = (rowData) => {
        return rowData.commonIssues.join(', ');
    };

    const serviceCallsBodyTemplate = (rowData) => {
        return (
            <span style={{ 
                color: rowData.serviceCalls >= 7 ? 'red' : rowData.serviceCalls >= 5 ? 'orange' : 'green',
                fontWeight: 'bold'
            }}>
                {rowData.serviceCalls}
            </span>
        );
    };

    return (
        <div>
            <div className="card mb-4">
                <h4 className="mb-3">{t('Recurrent Failures Summary')}</h4>
                <div className="grid">
                    <div className="col-12 md:col-4">
                        <Card>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-primary">{recurrentFailures.length}</div>
                                <div className="text-color-secondary">{t('Machines with Recurrent Issues')}</div>
                            </div>
                        </Card>
                    </div>
                    <div className="col-12 md:col-4">
                        <Card>
                            <div className="text-center">
                                <div className="text-2xl font-bold" style={{ color: '#F44336' }}>
                                    {recurrentFailures.filter(r => r.status === 'High Priority').length}
                                </div>
                                <div className="text-color-secondary">{t('High Priority')}</div>
                            </div>
                        </Card>
                    </div>
                    <div className="col-12 md:col-4">
                        <Card>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-primary">
                                    {recurrentFailures.reduce((sum, r) => sum + r.serviceCalls, 0)}
                                </div>
                                <div className="text-color-secondary">{t('Total Service Calls')}</div>
                            </div>
                        </Card>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h4>{t('Machines with Recurrent Failures (>5 service calls in 15 days)')}</h4>
                </div>
                <DataTable
                    value={recurrentFailures}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 20]}
                    emptyMessage={t('No recurrent failures found.')}
                    responsiveLayout="scroll"
                >
                    <Column field="machineId" header={t('Machine ID')} />
                    <Column field="machineType" header={t('Machine Type')} />
                    <Column field="customer" header={t('Customer')} />
                    <Column field="location" header={t('Location')} />
                    <Column 
                        field="serviceCalls" 
                        header={t('Service Calls')} 
                        body={serviceCallsBodyTemplate}
                    />
                    <Column field="daysPeriod" header={t('Days Period')} />
                    <Column field="lastServiceDate" header={t('Last Service Date')} />
                    <Column 
                        field="commonIssues" 
                        header={t('Common Issues')} 
                        body={issuesBodyTemplate}
                    />
                    <Column field="status" header={t('Status')} body={statusBodyTemplate} />
                </DataTable>
            </div>
        </div>
    );
};

export default RecurrentFailuresReport;
